// client/src/components/pages/LexiStar/Dwell/DwellLimits.tsx
//
// Dwell limits editor (ST-00188 M4-D, T7-S04; m4-contracts §3
// lexistar.dwell_limits, §4 GET/POST/PATCH /dwell-limits): one limit per site
// and context (loading / offloading / waiting / weighbridge), in minutes, with
// an optional minimum loading time for loading. The exceedance job compares
// each day's median dwell with these. Limits are versioned (PATCH carries the
// version read); the contract gives no delete — a limit is changed, not
// removed.

import { useState } from 'react';
import { FaPen, FaPlus } from 'react-icons/fa';
import type { DwellLimit, DwellLimitInput } from '../../../../types/lexistar-analytics';
import { ThemedSelect } from '../../../../types/commonComponents';
import { cardClasses } from '../Dashboard/analyticsAtoms';
import { CONTEXTS, contextLabel, draftOf, emptyDraft, fmtMin, validateDraft, type LimitDraft } from './dwellLogic';

export interface SiteOption {
  location_id: number;
  location_name: string;
}

const inputClass =
  'w-full px-2.5 py-1.5 border border-slate-300 rounded-md bg-surface text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

function DraftFields({
  draft,
  onChange,
  sites,
  lockSite,
  idPrefix,
}: {
  draft: LimitDraft;
  onChange: (d: LimitDraft) => void;
  sites: SiteOption[];
  lockSite: boolean;
  idPrefix: string;
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 items-end">
      <div>
        <label htmlFor={`${idPrefix}-site`} className="block text-[11px] font-medium text-slate-600 mb-1">
          Site
        </label>
        {lockSite ? (
          <p id={`${idPrefix}-site`} className="text-sm font-medium text-slate-900 py-1.5">
            {sites.find((s) => String(s.location_id) === draft.location_id)?.location_name || `Site ${draft.location_id}`}
          </p>
        ) : (
          <ThemedSelect
            id={`${idPrefix}-site`}
            aria-label="Site"
            value={draft.location_id}
            onChange={(e) => onChange({ ...draft, location_id: e.target.value })}
            className="text-sm"
          >
            <option value="">Choose a site...</option>
            {sites.map((s) => (
              <option key={s.location_id} value={String(s.location_id)}>
                {s.location_name}
              </option>
            ))}
          </ThemedSelect>
        )}
      </div>
      <div>
        <label htmlFor={`${idPrefix}-context`} className="block text-[11px] font-medium text-slate-600 mb-1">
          Limit for
        </label>
        {lockSite ? (
          <p id={`${idPrefix}-context`} className="text-sm text-slate-900 py-1.5">
            {contextLabel(draft.context)}
          </p>
        ) : (
          <ThemedSelect
            id={`${idPrefix}-context`}
            aria-label="Limit for"
            value={draft.context}
            onChange={(e) =>
              onChange({
                ...draft,
                context: e.target.value as LimitDraft['context'],
                min_loading_min: e.target.value === 'AT_SOURCE' ? draft.min_loading_min : '',
              })
            }
            className="text-sm"
          >
            <option value="">Choose...</option>
            {CONTEXTS.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </ThemedSelect>
        )}
      </div>
      <div>
        <label htmlFor={`${idPrefix}-max`} className="block text-[11px] font-medium text-slate-600 mb-1">
          Limit (min)
        </label>
        <input
          id={`${idPrefix}-max`}
          type="number"
          inputMode="numeric"
          min={1}
          step={1}
          value={draft.max_min}
          onChange={(e) => onChange({ ...draft, max_min: e.target.value })}
          className={inputClass}
        />
      </div>
      <div>
        <label htmlFor={`${idPrefix}-minload`} className="block text-[11px] font-medium text-slate-600 mb-1">
          Min. loading (min)
        </label>
        <input
          id={`${idPrefix}-minload`}
          type="number"
          inputMode="numeric"
          min={0}
          step={1}
          value={draft.min_loading_min}
          disabled={draft.context !== 'AT_SOURCE'}
          placeholder={draft.context === 'AT_SOURCE' ? 'optional' : 'loading only'}
          onChange={(e) => onChange({ ...draft, min_loading_min: e.target.value })}
          className={`${inputClass} disabled:bg-slate-50 disabled:text-slate-400`}
        />
      </div>
    </div>
  );
}

export default function DwellLimits({
  limits,
  sites,
  canManage,
  onCreate,
  onUpdate,
  loading,
}: {
  limits: DwellLimit[];
  sites: SiteOption[];
  canManage: boolean;
  onCreate: (body: DwellLimitInput) => Promise<void>;
  onUpdate: (limit: DwellLimit, body: DwellLimitInput) => Promise<void>;
  loading: boolean;
}) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<LimitDraft>(emptyDraft());
  const [editing, setEditing] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<LimitDraft>(emptyDraft());
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const sorted = [...limits].sort(
    (a, b) =>
      (a.location_name || '').localeCompare(b.location_name || '') ||
      CONTEXTS.findIndex((c) => c.id === a.context) - CONTEXTS.findIndex((c) => c.id === b.context)
  );

  const save = async (run: () => Promise<void>, done: () => void) => {
    setSaving(true);
    setError(null);
    try {
      await run();
      done();
    } catch (err) {
      const e = err as { response?: { status?: number; data?: { error?: string; message?: string } }; message?: string };
      setError(
        e.response?.status === 409
          ? 'Someone changed this limit while you were editing it. The list is refreshed — check it and try again.'
          : e.response?.data?.error || e.response?.data?.message || e.message || 'The limit could not be saved.'
      );
    } finally {
      setSaving(false);
    }
  };

  const submitNew = () => {
    const v = validateDraft(draft, limits);
    if (!v.ok) {
      setError(v.error);
      return;
    }
    save(
      () => onCreate(v.body),
      () => {
        setAdding(false);
        setDraft(emptyDraft());
      }
    );
  };
  const submitEdit = (l: DwellLimit) => {
    const v = validateDraft(editDraft, limits, l.id);
    if (!v.ok) {
      setError(v.error);
      return;
    }
    save(
      () => onUpdate(l, v.body),
      () => setEditing(null)
    );
  };

  return (
    <section className={`${cardClasses} p-4`} aria-labelledby="dwell-limits-title">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div>
          <h2 id="dwell-limits-title" className="text-sm font-semibold text-slate-900">
            Dwell limits
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            A day is an exceedance when the median dwell for that site and context runs past its limit.
          </p>
        </div>
        {canManage && !adding && (
          <button
            type="button"
            onClick={() => {
              setAdding(true);
              setEditing(null);
              setError(null);
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 rounded-lg shadow-sm"
          >
            <FaPlus size={10} aria-hidden="true" /> Add a limit
          </button>
        )}
      </div>

      {error && (
        <p role="alert" className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {error}
        </p>
      )}

      {adding && (
        <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3" role="group" aria-label="New dwell limit">
          <DraftFields draft={draft} onChange={setDraft} sites={sites} lockSite={false} idPrefix="new-limit" />
          <div className="flex justify-end gap-2 mt-3">
            <button
              type="button"
              onClick={() => {
                setAdding(false);
                setDraft(emptyDraft());
                setError(null);
              }}
              className="px-3 py-1.5 text-sm font-medium text-slate-700 hover:text-slate-900"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submitNew}
              disabled={saving}
              className="px-3 py-1.5 text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 rounded-lg disabled:bg-slate-300"
            >
              {saving ? 'Saving…' : 'Save limit'}
            </button>
          </div>
        </div>
      )}

      {loading && !limits.length ? (
        <p className="text-sm text-slate-500 py-4">Loading limits…</p>
      ) : !sorted.length ? (
        <p className="text-sm text-slate-600 py-4">
          No dwell limits yet. {canManage ? 'Add one per site and context to have exceedances flagged.' : 'An administrator sets them.'}
        </p>
      ) : (
        <div className="overflow-x-auto -mx-1">
          <table className="min-w-full text-sm" aria-label="Dwell limits">
            <thead>
              <tr className="text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide border-b border-slate-200">
                <th scope="col" className="px-2 py-2">
                  Site
                </th>
                <th scope="col" className="px-2 py-2">
                  For
                </th>
                <th scope="col" className="px-2 py-2 text-right">
                  Limit
                </th>
                {canManage && <th scope="col" className="px-2 py-2 w-14" aria-label="Actions" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sorted.map((l) =>
                editing === l.id ? (
                  <tr key={l.id}>
                    <td colSpan={canManage ? 4 : 3} className="px-2 py-3">
                      <div role="group" aria-label={`Edit the ${contextLabel(l.context).toLowerCase()} limit at ${l.location_name || l.location_id}`}>
                        <DraftFields draft={editDraft} onChange={setEditDraft} sites={sites} lockSite idPrefix={`edit-${l.id}`} />
                        <div className="flex justify-end gap-2 mt-2">
                          <button
                            type="button"
                            onClick={() => {
                              setEditing(null);
                              setError(null);
                            }}
                            className="px-3 py-1.5 text-sm font-medium text-slate-700 hover:text-slate-900"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={() => submitEdit(l)}
                            disabled={saving}
                            className="px-3 py-1.5 text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 rounded-lg disabled:bg-slate-300"
                          >
                            {saving ? 'Saving…' : 'Save'}
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr key={l.id}>
                    <td className="px-2 py-2 text-slate-900">{l.location_name || `Site ${l.location_id}`}</td>
                    <td className="px-2 py-2 text-slate-700">{contextLabel(l.context)}</td>
                    <td className="px-2 py-2 text-right tabular-nums whitespace-nowrap">
                      <span className="block text-slate-900 font-medium">{fmtMin(l.max_min)}</span>
                      {l.min_loading_min !== null && l.min_loading_min !== undefined && (
                        <span className="block text-[11px] text-slate-500">min. loading {fmtMin(l.min_loading_min)}</span>
                      )}
                    </td>
                    {canManage && (
                      <td className="px-2 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => {
                            setEditing(l.id);
                            setEditDraft(draftOf(l));
                            setAdding(false);
                            setError(null);
                          }}
                          className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:underline"
                          aria-label={`Edit the ${contextLabel(l.context).toLowerCase()} limit at ${l.location_name || l.location_id}`}
                        >
                          <FaPen size={9} aria-hidden="true" /> Edit
                        </button>
                      </td>
                    )}
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
